import { useEffect, useMemo, useRef } from 'react'
import { ReactRenderer } from '@tiptap/react'
import type {
  SuggestionProps,
  SuggestionKeyDownProps,
} from '@tiptap/suggestion'
import {
  computePosition,
  autoUpdate,
  flip,
  shift,
  offset,
} from '@floating-ui/dom'
import type { MentionUser } from '../../types'
import { MentionList } from './index'
import type { MentionListHandle } from './index'

export function useMentionSuggestion(users: MentionUser[]) {
  const usersRef = useRef<MentionUser[]>(users)
  usersRef.current = users

  const activePopupRef = useRef<{
    element: HTMLElement | null
    cleanup: (() => void) | null
    component: ReactRenderer<MentionListHandle> | null
  }>({ element: null, cleanup: null, component: null })

  useEffect(() => {
    return () => {
      activePopupRef.current.cleanup?.()
      activePopupRef.current.element?.remove()
      activePopupRef.current.component?.destroy()
    }
  }, [])

  return useMemo(
    () => ({
      char: '@',
      items: ({ query }: { query: string }) => {
        const q = query.toLowerCase()
        return usersRef.current
          .filter(user =>
            [user.label, user.display, user.description].some(field =>
              field?.toLowerCase().includes(q),
            ),
          )
          .slice(0, 8)
      },
      render: () => {
        let component: ReactRenderer<MentionListHandle> | null = null
        let element: HTMLElement | null = null
        let cleanup: (() => void) | null = null
        let getRect: (() => DOMRect) | null = null

        const positionEl = () => {
          if (!element || !getRect) return
          const virtualEl = { getBoundingClientRect: getRect }
          computePosition(virtualEl, element, {
            placement: 'bottom-start',
            middleware: [offset(4), flip(), shift({ padding: 8 })],
          }).then(({ x, y }) => {
            if (!element) return
            element.style.transform = `translate(${Math.round(
              x,
            )}px, ${Math.round(y)}px)`
          })
        }

        const teardown = () => {
          cleanup?.()
          element?.remove()
          component?.destroy()
          cleanup = null
          element = null
          getRect = null
          component = null
        }

        return {
          onStart: (props: SuggestionProps<MentionUser>) => {
            teardown()

            component = new ReactRenderer(MentionList, {
              props,
              editor: props.editor,
            })

            if (!props.clientRect) return

            element = document.createElement('div')
            element.style.position = 'absolute'
            element.style.top = '0'
            element.style.left = '0'
            element.style.zIndex = '9999'
            element.appendChild(component.element)
            // Appended to body rather than a portal because ReactRenderer
            // renders outside the main React tree — createPortal can't be used here.
            document.body.appendChild(element)

            getRect = props.clientRect as () => DOMRect
            const virtualEl = { getBoundingClientRect: getRect }

            function handleClickOutside(event: MouseEvent) {
              if (element && !element.contains(event.target as Node)) {
                teardown()
              }
            }
            document.addEventListener('mousedown', handleClickOutside)

            // autoUpdate returns a cleanup function
            const stopAutoUpdate = autoUpdate(virtualEl, element, positionEl)
            cleanup = () => {
              document.removeEventListener('mousedown', handleClickOutside)
              stopAutoUpdate()
            }

            activePopupRef.current = { element, cleanup, component }
          },

          onUpdate: (props: SuggestionProps<MentionUser>) => {
            component?.updateProps(props)
            if (props.clientRect) {
              getRect = props.clientRect as () => DOMRect
              positionEl()
            }
          },

          onKeyDown: (props: SuggestionKeyDownProps) => {
            if (props.event.key === 'Escape') {
              teardown()
              return true
            }
            return component?.ref?.onKeyDown(props) ?? false
          },

          onExit: () => {
            teardown()
            activePopupRef.current = {
              element: null,
              cleanup: null,
              component: null,
            }
          },
        }
      },
    }),
    [],
  )
}
